/**
 * "Enable notifications" card (PWA Stage 2).
 *
 * Contextual permission ask — renders only when it can actually work:
 *  - hidden when push is unsupported, unconfigured (no VAPID key), already
 *    enabled on this device, permission is hard-denied, or dismissed
 *  - on iOS, push exists ONLY inside the installed app; in a Safari tab
 *    this renders nothing (the InstallPrompt is the path there — a dead
 *    "enable" button would be worse than none)
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  pushSupported,
  vapidPublicKey,
  getExistingSubscription,
  enablePush,
} from '../../api/push';
import { getErrorMessage } from '../../api/client';

const DISMISS_KEY = 'pushCardDismissedAt';
const DISMISS_DAYS = 30;

function isIosBrowserTab(): boolean {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;
  return ios && !standalone;
}

export default function EnablePushCard() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
    if (!pushSupported() || !vapidPublicKey()) return;
    if (isIosBrowserTab()) return;
    if (Notification.permission === 'denied') return;

    getExistingSubscription().then((sub) => {
      if (!sub) setVisible(true);
    });
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Notifications on. You'll hear about new assignments here.");
      setVisible(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-3">
      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">Turn on notifications</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Get a ping on this device for new assignments and schedule changes. Follows the same preferences
          as your emails.
        </p>
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={enable}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Enabling…' : 'Enable notifications'}
          </button>
          <button type="button" onClick={dismiss} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
